import Vue from 'vue';
import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import PipelinesAPI from '../../api/pipelines';
import ContactAPI from '../../api/contacts';

export const state = {
  records: [],
  stages: {},
  contactStages: {},
  uiFlags: {
    isFetching: false,
    isFetchingStages: false,
    isSearching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isDuplicating: false,
  },
};

export const getters = {
  getPipelines(_state) {
    return _state.records;
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getPipelineById: _state => id => {
    return _state.records.find(pipeline => pipeline.id === Number(id));
  },
  getStagesByPipelineId: _state => pipelineId => {
    // Convert to number to ensure consistent key lookup
    const id = Number(pipelineId);
    return _state.stages[id] || [];
  },
  getDefaultPipeline(_state) {
    return _state.records.find(pipeline => pipeline.is_default);
  },
  getActivePipelines(_state) {
    return _state.records.filter(pipeline => pipeline.is_active);
  },
  getContactStages: _state => contactId => {
    return _state.contactStages[contactId] || [];
  },
};

export const actions = {
  // includeInactive solo lo usa Ajustes → Pipelines. El resto de las pantallas
  // lee getActivePipelines, así que aunque queden inactivos en el store no los
  // muestran.
  get: async function getPipelines(
    { commit },
    { includeInactive = false } = {}
  ) {
    commit(types.SET_PIPELINE_UI_FLAG, { isFetching: true });
    try {
      const response = await PipelinesAPI.get({ includeInactive });
      commit(types.SET_PIPELINES, response.data);
    } catch (error) {
      // Handle error
    } finally {
      commit(types.SET_PIPELINE_UI_FLAG, { isFetching: false });
    }
  },

  create: async function createPipeline({ commit }, pipelineData) {
    commit(types.SET_PIPELINE_UI_FLAG, { isCreating: true });
    try {
      const response = await PipelinesAPI.create(pipelineData);
      commit(types.ADD_PIPELINE, response.data);
      return response.data;
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error.message;
      throw new Error(errorMessage);
    } finally {
      commit(types.SET_PIPELINE_UI_FLAG, { isCreating: false });
    }
  },

  update: async function updatePipeline({ commit }, { id, ...updateData }) {
    commit(types.SET_PIPELINE_UI_FLAG, { isUpdating: true });
    try {
      const response = await PipelinesAPI.update(id, updateData);
      commit(types.EDIT_PIPELINE, response.data);
      return response.data;
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error.message;
      throw new Error(errorMessage);
    } finally {
      commit(types.SET_PIPELINE_UI_FLAG, { isUpdating: false });
    }
  },

  delete: async function deletePipeline({ commit }, id) {
    commit(types.SET_PIPELINE_UI_FLAG, { isDeleting: true });
    try {
      await PipelinesAPI.delete(id);
      commit(types.DELETE_PIPELINE, id);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error.message;
      throw new Error(errorMessage);
    } finally {
      commit(types.SET_PIPELINE_UI_FLAG, { isDeleting: false });
    }
  },

  duplicate: async function duplicatePipeline({ commit }, id) {
    commit(types.SET_PIPELINE_UI_FLAG, { isDuplicating: true });
    try {
      const response = await PipelinesAPI.duplicate(id);
      commit(types.ADD_PIPELINE, response.data);
      return response.data;
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error.message;
      throw new Error(errorMessage);
    } finally {
      commit(types.SET_PIPELINE_UI_FLAG, { isDuplicating: false });
    }
  },

  // Stage-related actions
  getStages: async function getStages({ commit }, pipelineId) {
    commit(types.SET_PIPELINE_UI_FLAG, { isFetchingStages: true });
    try {
      const response = await PipelinesAPI.getStages(pipelineId);
      commit(types.SET_PIPELINE_STAGES, { pipelineId, stages: response.data });
      return response.data;
    } catch (error) {
      console.error('Error fetching stages:', error);
      throw new Error(error?.response?.data?.message || error.message);
    } finally {
      commit(types.SET_PIPELINE_UI_FLAG, { isFetchingStages: false });
    }
  },
  
  getStagesWithContacts: async function getStagesWithContacts(
    { commit },
    { pipelineId, enteredAfter, enteredBefore, dateBasis }
  ) {
    commit(types.SET_PIPELINE_UI_FLAG, { isFetchingStages: true });
    try {
      const response = await PipelinesAPI.getStagesWithContacts(pipelineId, {
        enteredAfter,
        enteredBefore,
        dateBasis,
      });
      commit(types.SET_PIPELINE_STAGES, { pipelineId, stages: response.data });
      return response.data;
    } catch (error) {
      console.error('Error fetching stages with contacts:', error);
      throw new Error(error?.response?.data?.message || error.message);
    } finally {
      commit(types.SET_PIPELINE_UI_FLAG, { isFetchingStages: false });
    }
  },

  createStage: async function createStage({ commit }, { pipelineId, stageData }) {
    try {
      const response = await PipelinesAPI.createStage(pipelineId, stageData);
      commit(types.ADD_PIPELINE_STAGE, { pipelineId, stage: response.data });
      return response.data;
    } catch (error) {
      throw new Error(error?.response?.data?.message || error.message);
    }
  },

  updateStage: async function updateStage({ commit }, { pipelineId, stageId, stageData }) {
    try {
      const response = await PipelinesAPI.updateStage(pipelineId, stageId, stageData);
      commit(types.EDIT_PIPELINE_STAGE, { pipelineId, stage: response.data });
      return response.data;
    } catch (error) {
      throw new Error(error?.response?.data?.message || error.message);
    }
  },

  deleteStage: async function deleteStage({ commit }, { pipelineId, stageId }) {
    try {
      await PipelinesAPI.deleteStage(pipelineId, stageId);
      commit(types.DELETE_PIPELINE_STAGE, { pipelineId, stageId });
    } catch (error) {
      throw new Error(error?.response?.data?.message || error.message);
    }
  },

  reorderStages: async function reorderStages({ commit }, { pipelineId, stageIds }) {
    try {
      const response = await PipelinesAPI.reorderStages(pipelineId, stageIds);
      commit(types.SET_PIPELINE_STAGES, { pipelineId, stages: response.data.stages });
      return response.data;
    } catch (error) {
      throw new Error(error?.response?.data?.message || error.message);
    }
  },

  // Contact movement actions
  moveContactToStage: async function moveContactToStage(
    _,
    { pipelineId, stageId, contactId, notes, lost }
  ) {
    try {
      const response = await PipelinesAPI.moveContactToStage(
        pipelineId,
        stageId,
        contactId,
        notes,
        lost
      );
      return response.data;
    } catch (error) {
      throw new Error(error?.response?.data?.message || error.message);
    }
  },

  bulkMoveContactsToStage: async function bulkMoveContactsToStage(
    _,
    { pipelineId, stageId, contactIds, notes, lost }
  ) {
    try {
      const response = await PipelinesAPI.bulkMoveContactsToStage(
        pipelineId,
        stageId,
        contactIds,
        notes,
        lost
      );
      return response.data;
    } catch (error) {
      throw new Error(error?.response?.data?.message || error.message);
    }
  },

  importAllContactsToStage: async function importAllContactsToStage({ commit }, { pipelineId, stageId }) {
    try {
      const response = await PipelinesAPI.importAllContactsToStage(pipelineId, stageId);
      return response.data;
    } catch (error) {
      throw new Error(error?.response?.data?.message || error.message);
    }
  },

  removeContactFromStage: async function removeContactFromStage({ commit }, { pipelineId, stageId, contactId }) {
    try {
      const response = await PipelinesAPI.removeContactFromStage(pipelineId, stageId, contactId);
      return response.data;
    } catch (error) {
      throw new Error(error?.response?.data?.message || error.message);
    }
  },

  fetchContactStages: async function fetchContactStages({ commit }, contactId) {
    try {
      const response = await ContactAPI.getStages(contactId);
      commit(types.SET_CONTACT_STAGES, { contactId, stages: response.data });
      return response.data;
    } catch (error) {
      // Contact may not be in any pipeline — not an error
      commit(types.SET_CONTACT_STAGES, { contactId, stages: [] });
      return [];
    }
  },

  moveContactStageFromConversation:
    async function moveContactStageFromConversation(
      { commit },
      { contactId, stageId, lost = {} }
    ) {
      try {
        const moved = await ContactAPI.moveToStage(contactId, stageId, lost);

        // Re-fetch contact stages to get updated state
        const response = await ContactAPI.getStages(contactId);
        commit(types.SET_CONTACT_STAGES, { contactId, stages: response.data });
        // E06: el selector de la conversación ofrece pausar a la IA si ya
        // movió a este contacto en el pipeline.
        return {
          stages: response.data,
          aiMovedBefore: Boolean(moved?.data?.ai_moved_before),
          aiMovesPaused: Boolean(moved?.data?.ai_moves?.paused),
        };
      } catch (error) {
        const wrapped = new Error(
          error?.response?.data?.message || error.message
        );
        wrapped.code = error?.response?.data?.code;
        throw wrapped;
      }
    },

  // Devuelve el valor guardado. En un 409 el error trae `code` y `current`
  // (el valor actual) para que la tarjeta lo muestre sin pisar nada.
  updateContactDeal: async function updateContactDeal(_, payload) {
    try {
      const response = await ContactAPI.updateDeal(payload.contactId, payload);
      return response.data;
    } catch (error) {
      const data = error?.response?.data || {};
      const wrapped = new Error(data.error || data.message || error.message);
      wrapped.code = data.code;
      wrapped.current = data.current;
      wrapped.status = error?.response?.status;
      throw wrapped;
    }
  },

  searchStagesWithContacts: async function searchStagesWithContacts(
    { commit },
    { pipelineId, query, enteredAfter, enteredBefore, dateBasis }
  ) {
    commit(types.SET_PIPELINE_UI_FLAG, { isSearching: true });
    try {
      const response = await PipelinesAPI.getStagesWithContacts(pipelineId, {
        search: query,
        enteredAfter,
        enteredBefore,
        dateBasis,
      });
      return response.data;
    } catch (error) {
      throw new Error(error?.response?.data?.message || error.message);
    } finally {
      commit(types.SET_PIPELINE_UI_FLAG, { isSearching: false });
    }
  },

  loadMoreStageContacts: async function loadMoreStageContacts(
    _,
    {
      pipelineId,
      stageId,
      page,
      perPage,
      enteredAfter,
      enteredBefore,
      dateBasis,
    }
  ) {
    try {
      const response = await PipelinesAPI.getStageContacts(pipelineId, stageId, {
        page,
        perPage,
        enteredAfter,
        enteredBefore,
        dateBasis,
      });
      return response.data;
    } catch (error) {
      throw new Error(error?.response?.data?.message || error.message);
    }
  },
};

// La columna "Etapas" de Ajustes → Pipelines lee `stages_count` del pipeline;
// al crear o borrar etapas desde el modal se mantiene al día sin recargar.
const syncStagesCount = (_state, pipelineId) => {
  const pipeline = _state.records.find(record => record.id === pipelineId);
  if (pipeline) {
    Vue.set(pipeline, 'stages_count', (_state.stages[pipelineId] || []).length);
  }
};

export const mutations = {
  [types.SET_PIPELINE_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.SET_PIPELINES]: MutationHelpers.set,
  [types.ADD_PIPELINE]: MutationHelpers.create,
  [types.EDIT_PIPELINE]: MutationHelpers.update,
  [types.DELETE_PIPELINE]: MutationHelpers.destroy,

  [types.SET_PIPELINE_STAGES](_state, { pipelineId, stages }) {
    // Ensure pipelineId is a number for consistent storage
    const id = Number(pipelineId);
    _state.stages = {
      ..._state.stages,
      [id]: stages,
    };
    syncStagesCount(_state, id);
  },

  [types.ADD_PIPELINE_STAGE](_state, { pipelineId, stage }) {
    const id = Number(pipelineId);
    const stages = _state.stages[id] || [];
    _state.stages = {
      ..._state.stages,
      [id]: [...stages, stage],
    };
    syncStagesCount(_state, id);
  },

  // Reemplaza la etapa con la respuesta del servidor, en un arreglo nuevo, para
  // que la lista del modal y el formulario al reabrirlo muestren lo guardado.
  [types.EDIT_PIPELINE_STAGE](_state, { pipelineId, stage }) {
    const id = Number(pipelineId);
    const stages = _state.stages[id] || [];
    if (!stages.some(s => s.id === stage.id)) return;
    _state.stages = {
      ..._state.stages,
      [id]: stages.map(s => (s.id === stage.id ? { ...s, ...stage } : s)),
    };
  },

  [types.DELETE_PIPELINE_STAGE](_state, { pipelineId, stageId }) {
    const id = Number(pipelineId);
    const stages = _state.stages[id] || [];
    _state.stages = {
      ..._state.stages,
      [id]: stages.filter(s => s.id !== stageId),
    };
    syncStagesCount(_state, id);
  },

  [types.SET_CONTACT_STAGES](_state, { contactId, stages }) {
    _state.contactStages = {
      ..._state.contactStages,
      [contactId]: stages,
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};